import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Flame, Target, Trophy, Zap, Mail, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Flashcard from '../components/Flashcard';
import { generateQuestion, ALL_KEYS } from '../lib/nashville_theory';
import useStore from '../store/useStore';
import { cn } from '../lib/utils';

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
var QUESTIONS_PER_SESSION = 10;
var FREE_SESSIONS_BEFORE_EMAIL = 3;
var DAILY_SESSIONS_AFTER_EMAIL = 5;
var STORAGE_KEY = 'qp_tracker';

function getTodayStr() {
  return new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
}

function getTracker() {
  try {
    var raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function saveTracker(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function initTracker() {
  var existing = getTracker();
  if (existing) return existing;
  return { sessionsBeforeEmail: 0, emailSubmitted: false, email: '', dailySessions: 0, dailyDate: getTodayStr() };
}

function getSessionsUsedToday(tracker) {
  if (tracker.dailyDate !== getTodayStr()) return 0;
  return tracker.dailySessions || 0;
}

function getSessionsRemaining(tracker) {
  if (!tracker.emailSubmitted) {
    return Math.max(0, FREE_SESSIONS_BEFORE_EMAIL - tracker.sessionsBeforeEmail);
  }
  var usedToday = getSessionsUsedToday(tracker);
  return Math.max(0, DAILY_SESSIONS_AFTER_EMAIL - usedToday);
}

function isDailyLimitReached(tracker) {
  if (!tracker.emailSubmitted) return false;
  return getSessionsUsedToday(tracker) >= DAILY_SESSIONS_AFTER_EMAIL;
}

function getPercentileData(accuracy) {
  if (accuracy === 100) return { pct: '1%', title: 'Number System Legend', emoji: '👑', color: 'text-amber-500', bg: 'bg-amber-50', msg: 'PERFECT SCORE! You absolutely crushed it. You are in the top 1% of all players!' };
  if (accuracy >= 90) return { pct: '5%', title: 'Sharp Ear', emoji: '🔥', color: 'text-orange-500', bg: 'bg-orange-50', msg: 'Incredible! You are in the top 5%. Your number system skills are on fire!' };
  if (accuracy >= 80) return { pct: '15%', title: 'Rising Star', emoji: '⭐', color: 'text-emerald-500', bg: 'bg-emerald-50', msg: 'Seriously impressive! Top 15%. You have got a strong foundation!' };
  if (accuracy >= 70) return { pct: '30%', title: 'Chord Crusher', emoji: '💪', color: 'text-blue-500', bg: 'bg-blue-50', msg: 'Nice work! Top 30%. You are building real fluency!' };
  if (accuracy >= 60) return { pct: '50%', title: 'Number Cruncher', emoji: '🎹', color: 'text-violet-500', bg: 'bg-violet-50', msg: 'Solid effort! Top 50%. Keep at it and you will climb fast!' };
  if (accuracy >= 40) return { pct: '65%', title: 'Groove Finder', emoji: '🎵', color: 'text-indigo-500', bg: 'bg-indigo-50', msg: 'You are getting the hang of it! More practice will level you up quick!' };
  return { pct: '80%', title: 'Fresh Start', emoji: '🌱', color: 'text-teal-500', bg: 'bg-teal-50', msg: 'Everyone starts here! The more you practice, the faster it clicks. Keep going!' };
}

function QuickPractice() {
  var navigate = useNavigate();
  var storeActions = useStore();

  var tracker = initTracker();

  var [phase, setPhase] = useState(function() {
    if (!tracker.emailSubmitted && tracker.sessionsBeforeEmail >= FREE_SESSIONS_BEFORE_EMAIL) return 'email_gate';
    if (tracker.emailSubmitted && isDailyLimitReached(tracker)) return 'locked';
    return 'setup';
  });
  var [selectedKeys, setSelectedKeys] = useState(['C', 'G', 'D']);
  var [currentQuestion, setCurrentQuestion] = useState(null);

  var sessionsRemaining = getSessionsRemaining(tracker);
  var [currentSessionNum, setCurrentSessionNum] = useState(1);
  var [questionInSession, setQuestionInSession] = useState(0);
  var [sessionCorrect, setSessionCorrect] = useState(0);
  var [sessionStreak, setSessionStreak] = useState(0);
  var [sessionBestStreak, setSessionBestStreak] = useState(0);

  var [totalCorrect, setTotalCorrect] = useState(0);
  var [totalAnswered, setTotalAnswered] = useState(0);

  var [leadEmail, setLeadEmail] = useState(tracker.email || '');
  var [leadName, setLeadName] = useState('');
  var [emailSubmitted, setEmailSubmitted] = useState(tracker.emailSubmitted || false);
  var [emailLoading, setEmailLoading] = useState(false);

  function toggleKey(key) {
    setSelectedKeys(function(prev) {
      if (prev.includes(key)) {
        return prev.length > 1 ? prev.filter(function(k) { return k !== key; }) : prev;
      }
      return prev.concat([key]);
    });
  }

  function startFirstSession() {
    storeActions.setTimerEnabled(true);
    storeActions.setTimerDuration(10);
    var q = generateQuestion(selectedKeys, 'chord_to_number');
    setCurrentQuestion(q);
    setPhase('playing');
  }

  function startNextSession() {
    setQuestionInSession(0);
    setSessionCorrect(0);
    setSessionStreak(0);
    setSessionBestStreak(0);
    setCurrentSessionNum(function(s) { return s + 1; });
    var q = generateQuestion(selectedKeys, 'chord_to_number');
    setCurrentQuestion(q);
    setPhase('playing');
  }

  var handleAnswer = useCallback(function(isCorrect) {
    var newQ = questionInSession + 1;
    setQuestionInSession(newQ);
    setTotalAnswered(function(t) { return t + 1; });

    if (isCorrect) {
      setSessionCorrect(function(c) { return c + 1; });
      setTotalCorrect(function(t) { return t + 1; });
      setSessionStreak(function(s) {
        var ns = s + 1;
        setSessionBestStreak(function(b) { return Math.max(b, ns); });
        return ns;
      });
    } else {
      setSessionStreak(0);
    }

    if (newQ >= QUESTIONS_PER_SESSION) {
      // Save session completion to localStorage
      var t = getTracker() || initTracker();
      if (!t.emailSubmitted) {
        t.sessionsBeforeEmail = (t.sessionsBeforeEmail || 0) + 1;
      } else {
        // Reset daily counter if new day
        if (t.dailyDate !== getTodayStr()) {
          t.dailySessions = 0;
          t.dailyDate = getTodayStr();
        }
        t.dailySessions = (t.dailySessions || 0) + 1;
      }
      saveTracker(t);
      setTimeout(function() { setPhase('session_result'); }, 1500);
    }
  }, [questionInSession]);

  var handleNext = useCallback(function() {
    if (questionInSession >= QUESTIONS_PER_SESSION) {
      setPhase('session_result');
      return;
    }
    var q = generateQuestion(selectedKeys, 'chord_to_number');
    setCurrentQuestion(q);
  }, [selectedKeys, questionInSession]);

  function submitEmail() {
    if (!leadEmail.trim() || !leadEmail.includes('@')) return;
    setEmailLoading(true);
    fetch(BACKEND_URL + '/api/leads/capture', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: leadEmail.trim(), name: leadName.trim(), source: localStorage.getItem('traffic_source') || 'quick_practice' })
    }).then(function() {
      setEmailSubmitted(true);
      setEmailLoading(false);
      var t = getTracker() || initTracker();
      t.emailSubmitted = true;
      t.email = leadEmail.trim();
      t.dailySessions = 0;
      t.dailyDate = getTodayStr();
      saveTracker(t);
      setPhase('setup');
    }).catch(function() {
      setEmailLoading(false);
    });
  }

  var sessionAccuracy = questionInSession > 0 ? Math.round((sessionCorrect / questionInSession) * 100) : 0;
  var overallAccuracy = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;

  // ---- LOCKED SCREEN (daily limit reached) ----
  if (phase === 'locked') {
    return (
      <div className="min-h-screen noise-bg flex items-center justify-center p-6" data-testid="locked-screen">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
          <div className="bg-card border-2 border-border rounded-3xl p-8 brutalist-shadow text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Daily Limit Reached!</h2>
            <p className="text-muted-foreground mb-6">
              You've used all <span className="font-bold text-foreground">{DAILY_SESSIONS_AFTER_EMAIL} free sessions</span> for today. Come back tomorrow for {DAILY_SESSIONS_AFTER_EMAIL} more!
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Want unlimited practice with no daily limits? Subscribe for full access including progress tracking, AI hints, and all 12 keys!
            </p>
            <div className="flex flex-col gap-3">
              <Button size="lg" onClick={function() { navigate('/auth'); }} className="w-full bg-accent hover:bg-accent/90 text-white" data-testid="locked-signup-btn">
                Get Unlimited Access — $5/month
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="ghost" onClick={function() { navigate('/'); }} data-testid="locked-home-btn">
                Back to Home
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---- EMAIL GATE SCREEN ----
  if (phase === 'email_gate') {
    return (
      <div className="min-h-screen noise-bg flex items-center justify-center p-6" data-testid="email-gate-screen">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
          <div className="bg-card border-2 border-border rounded-3xl p-8 brutalist-shadow text-center">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-accent" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Want {DAILY_SESSIONS_AFTER_EMAIL} Free Sessions Every Day?</h2>
            <p className="text-muted-foreground mb-6">
              You've used your {FREE_SESSIONS_BEFORE_EMAIL} free sessions. Enter your email to unlock <span className="font-bold text-foreground">{DAILY_SESSIONS_AFTER_EMAIL} sessions per day</span> and get practice tips!
            </p>
            <div className="space-y-3 mb-4">
              <Input type="text" placeholder="Your name (optional)" value={leadName} onChange={function(e) { setLeadName(e.target.value); }} className="bg-secondary" data-testid="gate-name-input" />
              <Input type="email" placeholder="Your email" value={leadEmail} onChange={function(e) { setLeadEmail(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') submitEmail(); }} className="bg-secondary" data-testid="gate-email-input" />
              <Button onClick={submitEmail} disabled={emailLoading || !leadEmail.trim()} className="w-full bg-accent hover:bg-accent/90 text-white" data-testid="gate-submit-btn">
                {emailLoading ? 'Sending...' : 'Unlock ' + DAILY_SESSIONS_AFTER_EMAIL + ' Daily Sessions'}
              </Button>
            </div>
            <div className="border-t border-border pt-4 mt-2">
              <Button variant="outline" className="w-full" onClick={function() { navigate('/auth'); }} data-testid="gate-signup-btn">
                Or sign up for unlimited access — $5/month
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---- SETUP SCREEN ----
  if (phase === 'setup') {
    return (
      <div className="min-h-screen noise-bg" data-testid="quick-practice-setup">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 md:px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={function() { navigate('/'); }} className="btn-press" data-testid="setup-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Quick Practice</h1>
            <span className="text-sm text-muted-foreground ml-auto">Chord → # mode</span>
          </div>
        </header>
        <main className="container mx-auto px-4 md:px-6 py-12 max-w-lg">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
            <div className="bg-card border-2 border-border rounded-2xl p-5 brutalist-shadow text-center">
              <Zap className="w-8 h-8 text-accent mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                <span className="font-bold text-foreground">{sessionsRemaining} session{sessionsRemaining !== 1 ? 's' : ''} remaining{emailSubmitted ? ' today' : ''}</span> with {QUESTIONS_PER_SESSION} questions each.
                <br />10-second timer per question. See how you rank!
              </p>
            </div>
            <div>
              <h2 className="text-lg font-bold mb-3">Select Keys</h2>
              <div className="grid grid-cols-4 gap-2" data-testid="quick-key-grid">
                {ALL_KEYS.map(function(k) {
                  return (
                    <button
                      key={k.name}
                      onClick={function() { toggleKey(k.name); }}
                      className={cn(
                        "py-2.5 px-3 rounded-xl text-sm font-mono font-semibold border-2 transition-all",
                        selectedKeys.includes(k.name)
                          ? "bg-accent text-white border-accent"
                          : "bg-card border-border hover:border-accent/50"
                      )}
                      data-testid={'quick-key-' + k.name}
                    >
                      {k.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-2">{selectedKeys.length} key{selectedKeys.length !== 1 ? 's' : ''} selected</p>
            </div>
            <Button size="lg" onClick={startFirstSession} className="w-full bg-accent hover:bg-accent/90 text-white rounded-xl" data-testid="start-quick-btn">
              <Zap className="w-5 h-5 mr-2" />
              Start Session {currentSessionNum}
            </Button>
          </motion.div>
        </main>
      </div>
    );
  }

  // ---- SESSION RESULT SCREEN ----
  if (phase === 'session_result') {
    var pData = getPercentileData(sessionAccuracy);
    var latestTracker = getTracker() || initTracker();
    var needsEmail = !latestTracker.emailSubmitted && latestTracker.sessionsBeforeEmail >= FREE_SESSIONS_BEFORE_EMAIL;
    var dailyDone = latestTracker.emailSubmitted && isDailyLimitReached(latestTracker);

    // After 3 sessions without email, force email gate
    if (needsEmail) {
      setPhase('email_gate');
    }
    // After daily limit reached, force locked
    if (dailyDone) {
      setPhase('locked');
    }

    return (
      <div className="min-h-screen noise-bg flex items-center justify-center p-6" data-testid="session-result-screen">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <div className="bg-card border-2 border-border rounded-3xl p-8 brutalist-shadow text-center">
            <div className="text-sm text-muted-foreground mb-1 font-mono">Session {currentSessionNum} Complete</div>
            <h2 className="text-2xl font-bold mb-4">Session Results</h2>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="p-3 bg-secondary rounded-xl">
                <div className="text-2xl font-bold font-mono">{sessionCorrect}/{QUESTIONS_PER_SESSION}</div>
                <div className="text-xs text-muted-foreground mt-1">Correct</div>
              </div>
              <div className="p-3 bg-secondary rounded-xl">
                <div className="text-2xl font-bold font-mono">{sessionAccuracy}%</div>
                <div className="text-xs text-muted-foreground mt-1">Accuracy</div>
              </div>
              <div className="p-3 bg-secondary rounded-xl">
                <div className="text-2xl font-bold font-mono">{sessionBestStreak}</div>
                <div className="text-xs text-muted-foreground mt-1">Best Streak</div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={cn("rounded-2xl p-5 mb-6", pData.bg)}
              data-testid="percentile-card"
            >
              <div className="text-3xl mb-1">{pData.emoji}</div>
              <div className={cn("text-lg font-bold mb-1", pData.color)}>{pData.title}</div>
              <div className={cn("text-sm font-semibold mb-2", pData.color)}>Top {pData.pct} of players</div>
              <p className="text-sm text-muted-foreground">{pData.msg}</p>
            </motion.div>

            {totalAnswered > QUESTIONS_PER_SESSION && (
              <div className="bg-secondary/50 rounded-xl p-3 mb-6 text-sm" data-testid="overall-stats">
                <span className="text-muted-foreground">Overall: </span>
                <span className="font-bold font-mono">{totalCorrect}/{totalAnswered}</span>
                <span className="text-muted-foreground"> correct ({overallAccuracy}%)</span>
              </div>
            )}

            <div className="flex flex-col gap-3" data-testid="next-session-actions">
              <Button size="lg" onClick={startNextSession} className="w-full bg-accent hover:bg-accent/90 text-white" data-testid="next-session-btn">
                Start Next Session
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="outline" onClick={function() { navigate('/auth'); }} data-testid="signup-early-btn">
                Sign up for unlimited access — $5/month
              </Button>
            </div>
          </div>

          <Button variant="ghost" onClick={function() { navigate('/'); }} className="mt-6 mx-auto block" data-testid="back-home-btn">
            ← Back to Home
          </Button>
        </motion.div>
      </div>
    );
  }

  // ---- PLAYING SCREEN ----
  return (
    <div className="min-h-screen noise-bg" data-testid="quick-practice-session">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={function() { navigate('/'); }} className="btn-press" data-testid="session-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold hidden md:block">Session {currentSessionNum}</h1>
          </div>

          <div className="flex items-center gap-2 text-sm font-mono" data-testid="question-counter">
            <span className="text-muted-foreground">Question</span>
            <span className="font-bold text-foreground">{Math.min(questionInSession + 1, QUESTIONS_PER_SESSION)}</span>
            <span className="text-muted-foreground">/ {QUESTIONS_PER_SESSION}</span>
          </div>

          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Target className="w-4 h-4 text-muted-foreground" />
              <span className="font-mono font-semibold">{sessionCorrect}</span>
            </div>
            {sessionStreak >= 2 && (
              <div className="flex items-center gap-1 text-accent">
                <Flame className="w-4 h-4" />
                <span className="font-mono font-semibold">{sessionStreak}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="w-full h-1.5 bg-muted">
        <motion.div
          className="h-full bg-accent"
          initial={{ width: 0 }}
          animate={{ width: (questionInSession / QUESTIONS_PER_SESSION * 100) + '%' }}
          transition={{ duration: 0.3 }}
        />
      </div>

      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col items-center justify-center min-h-[55vh]">
          <AnimatePresence>
            {sessionStreak >= 3 && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex items-center gap-2 mb-6 px-4 py-2 bg-accent/10 rounded-full"
              >
                <Flame className="w-5 h-5 text-accent" />
                <span className="font-bold text-accent">{sessionStreak} streak!</span>
              </motion.div>
            )}
          </AnimatePresence>

          {currentQuestion && (
            <Flashcard
              question={currentQuestion}
              onAnswer={handleAnswer}
              onNext={handleNext}
            />
          )}
        </div>
      </main>
    </div>
  );
}

export default QuickPractice;
